'use client'

import { CheckCircle, ChevronDown, Download, ExternalLink, Upload } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleDriveUploadFile } from 'sprites-types/workflow/tools/googleDriveUploadFile'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleDriveUploadFileDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isFileInputExpanded, setIsFileInputExpanded] = useState(false)
  const schema = getToolResultSchema(googleDriveUploadFile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const fileName = result.response?.name || inputs.name || inputs.fileName || 'Uploaded file'
  const fileId = result.response?.id
  const googleDriveUrl = result.response?.googleDriveUrl
  const webViewLink =
    googleDriveUrl || (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null)
  const cdnUrl = result.response?.cdnUrl
  const size = (result.response as any)?.size || (result.response as any)?.fileSize || 0
  const mimeType = result.response?.mimeType || inputs.mimeType || ''

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return ''
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-green-100 p-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <Upload className="h-4 w-4 text-blue-600" />
            <div className="text-sm font-semibold text-gray-900">File uploaded to Google Drive</div>
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
            {webViewLink && (
              <a
                href={webViewLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="font-medium">Google Drive URL</span>
              </a>
            )}
            {cdnUrl && (
              <a
                href={cdnUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-green-600 hover:text-green-800"
              >
                <Download className="h-4 w-4" />
                <span className="font-medium">Direct Download</span>
              </a>
            )}
          </div>

          <div>
            <details className="w-full" open={isFileInputExpanded}>
              <summary
                className="flex w-full cursor-pointer list-none items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                onClick={(e) => {
                  e.preventDefault()
                  setIsFileInputExpanded(!isFileInputExpanded)
                }}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isFileInputExpanded ? 'rotate-90' : ''
                  }`}
                />
                <span>File Details</span>
              </summary>
              {isFileInputExpanded && (
                <div className="mt-3 space-y-2">
                  <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 text-xs">
                    <div className="space-y-1 text-gray-700">
                      <div>
                        <span className="font-medium">File Name:</span> {fileName}
                      </div>
                      {size > 0 && (
                        <div>
                          <span className="font-medium">File Size:</span>{' '}
                          {formatFileSize(parseInt(size.toString()))}
                        </div>
                      )}
                      {mimeType && (
                        <div>
                          <span className="font-medium">MIME Type:</span> {mimeType}
                        </div>
                      )}
                      {inputs.name && (
                        <div>
                          <span className="font-medium">Original Name:</span> {inputs.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </details>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGoogleDriveUploadFile(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleDriveUploadFileToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleDriveUploadFile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const fileName = result.response?.name || inputs.name || inputs.fileName || 'Uploaded file'
  const fileId = result.response?.id
  const googleDriveUrl = result.response?.googleDriveUrl
  const webViewLink =
    googleDriveUrl || (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null)
  const cdnUrl = result.response?.cdnUrl
  const size = (result.response as any)?.size || (result.response as any)?.fileSize || 0
  const mimeType = result.response?.mimeType || inputs.mimeType || ''

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return ''
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return `## File Uploaded to Google Drive

**File Name:** ${fileName}
${fileId ? `**File ID:** ${fileId}` : ''}
${size > 0 ? `**Size:** ${formatFileSize(parseInt(size.toString()))}` : ''}
${mimeType ? `**MIME Type:** ${mimeType}` : ''}
${webViewLink ? `**Google Drive URL:** [Open in Drive](${webViewLink})` : ''}
${cdnUrl ? `**Direct Download:** ${cdnUrl}` : ''}

---
`
}

export const GoogleDriveUploadFileDisplay = createWorkflowToolComponent(
  GoogleDriveUploadFileDisplayComponent,
  downloadGoogleDriveUploadFile,
  googleDriveUploadFileToMarkdown
)
